import Logger from "../../../utils/logger";
import { getPackage } from "../../../api/v2/itinerary-review/getPackage";
import { store } from "../../../store";
import { actions as packageIdentityActions } from "../../package-identity/actions";

interface PollPackageVersionArgs {
  searchKey: string;
  targetVersion?: number;
  maxAttempts?: number;
}

export const pollPackageVersion = async ({
  searchKey,
  targetVersion,
  maxAttempts = 3,
}: PollPackageVersionArgs): Promise<void> => {
  // Mark the poll as in flight for every call, even when it's about to return
  // immediately below — Review/Book's redirect-decision gates need "polling"
  // to always precede "settled" so they never read a stale "idle"/"settled"
  // value as "nothing is loading" before this call has had a chance to run.
  store.dispatch(packageIdentityActions.setPackagePollState("polling"));

  try {
    if (targetVersion == null) {
      return;
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop -- each poll attempt must wait on the previous one's result before deciding whether to retry; the attempts are inherently sequential, not parallelizable
        const response = await getPackage({ searchKey: { value: searchKey } });
        store.dispatch(
          packageIdentityActions.setPackageDisplayData(
            searchKey,
            response.packageDisplayData
          )
        );
        if (response.packageDisplayData.version >= targetVersion) return;
      } catch (e) {
        Logger.debug(e);
      }
    }

    Logger.debug(
      `pollPackageVersion: exhausted ${maxAttempts} attempts without reaching target version ${targetVersion} for searchKey ${searchKey}`
    );
  } finally {
    // Fires on every exit path above (immediate return, success return,
    // exhausted-attempts fallthrough) so "polling" never gets stuck.
    store.dispatch(packageIdentityActions.setPackagePollState("settled"));
  }
};
